import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  getGetCustomReportV2QueryKey,
  getGetReportSectionQueryKey,
  getListCustomReportsByCaseQueryKey,
  useGetReportSection,
  useRegenerateSection,
  useUpdateReportSection,
} from "~/api/custom-reports/custom-reports";
import type { ReportV2 } from "~/api/model";
import { generateIdempotencyKey } from "./reportUtils";

export const useSectionData = (
  organizationId: string,
  caseId: string,
  reportId: string,
  sectionId: string,
  sectionStatus: string | undefined,
  isExpanded: boolean,
  isVisible: boolean
) => {
  const queryClient = useQueryClient();

  const { data: sectionData, isLoading } = useGetReportSection(
    organizationId,
    sectionId,
    {
      query: {
        enabled:
          isExpanded &&
          isVisible &&
          !!sectionId &&
          sectionStatus === "completed",
      },
    }
  );

  const { mutate: regenerateSection } = useRegenerateSection({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: getGetCustomReportV2QueryKey(organizationId, reportId),
        });

        const previousReport = queryClient.getQueryData(
          getGetCustomReportV2QueryKey(organizationId, reportId)
        );

        queryClient.setQueryData(
          getGetCustomReportV2QueryKey(organizationId, reportId),
          (old: ReportV2 | undefined) => {
            if (!old?.reportSections) return old;

            return {
              ...old,
              reportSections: old.reportSections.map((section) =>
                section.id === variables.reportSectionId
                  ? { ...section, status: "pending" as const }
                  : section
              ),
            };
          }
        );

        return { previousReport };
      },
      onError: (_err, _variables, context) => {
        if (context?.previousReport) {
          queryClient.setQueryData(
            getGetCustomReportV2QueryKey(organizationId, reportId),
            context.previousReport
          );
        }
      },
      onSuccess: async (_data, variables) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetCustomReportV2QueryKey(organizationId, reportId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetReportSectionQueryKey(
              organizationId,
              variables.reportSectionId
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getListCustomReportsByCaseQueryKey(
              organizationId,
              caseId
            ),
          }),
        ]);
      },
    },
  });

  const { mutate: updateReportSection } = useUpdateReportSection({
    mutation: {
      onSuccess: async (_data, variables) => {
        await queryClient.invalidateQueries({
          queryKey: getGetReportSectionQueryKey(
            organizationId,
            variables.reportSectionId
          ),
        });
      },
    },
  });

  const handleRegenerate = useCallback(() => {
    if (!sectionId) return;
    regenerateSection({
      organizationId,
      reportSectionId: sectionId,
      data: {
        caseId,
        idempotencyKey: generateIdempotencyKey(),
      },
    });
  }, [regenerateSection, organizationId, caseId, sectionId]);

  const handleEdit = useCallback(
    async (content: string): Promise<void> => {
      if (!sectionId) return;
      return new Promise((resolve, reject) => {
        updateReportSection(
          {
            organizationId,
            reportSectionId: sectionId,
            data: { content },
          },
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          }
        );
      });
    },
    [updateReportSection, organizationId, sectionId]
  );

  return {
    sectionData,
    isLoading,
    handleRegenerate,
    handleEdit,
  };
};
